import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS } from "@/data/content";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.4 });

  const linkCls = ({ isActive }) =>
    `font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
      isActive ? "text-glow" : "text-slate-400 hover:text-slate-100"
    }`;

  return (
    <header className="fixed inset-x-0 top-0 z-50" data-testid="site-nav">
      <div className="border-b border-line/50 bg-ink/85 backdrop-blur-md">
        <div className="wrap flex h-16 items-center justify-between lg:h-[72px]">
          <Logo />
          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkCls} data-testid={l.testId}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-3 lg:flex">
            <Link
              to="/#fieldnotes"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 transition-colors duration-300 hover:text-glow"
              data-testid="nav-cta-fieldnotes"
            >
              Get the Fieldnotes
            </Link>
            <Link to="/contact" className="btn-primary !px-5 !py-2.5" data-testid="nav-cta-conversation">
              Start a conversation
            </Link>
          </div>
          <button
            className="flex h-10 w-10 items-center justify-center text-slate-200 lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-button"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        <motion.div
          className="h-px origin-left bg-glow/70"
          style={{ scaleX }}
          data-testid="scroll-progress-bar"
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 top-16 z-40 flex flex-col justify-between bg-ink/[0.985] backdrop-blur-lg lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="wrap flex flex-col gap-2 pt-10" aria-label="Mobile">
              {[...NAV_LINKS, { label: "Selected Work", to: "/work", testId: "nav-link-work" }].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `group flex items-center justify-between border-b border-line/50 py-5 text-2xl font-medium tracking-tight ${
                        isActive ? "text-glow" : "text-slate-200"
                      }`
                    }
                    data-testid={`mobile-${l.testId}`}
                  >
                    {l.label}
                    <ArrowUpRight size={20} className="text-slate-600 transition-colors group-hover:text-glow" />
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="wrap flex flex-col gap-3 pb-12"
            >
              <Link to="/contact" onClick={() => setOpen(false)} className="btn-primary w-full" data-testid="mobile-cta-conversation">
                Start a conversation
              </Link>
              <Link
                to="/#fieldnotes"
                onClick={() => setOpen(false)}
                className="btn-ghost w-full"
                data-testid="mobile-cta-fieldnotes"
              >
                Get the Fieldnotes
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
